import { useRef, useState } from "react";
import {
	Box,
	Button,
	Typography,
	Modal,
	MenuItem,
	TextField,
	Select,
	Paper,
	InputLabel,
	FormControl,
	Dialog,
	Divider,
	DialogContent,
	CircularProgress,
	FormHelperText,
	DialogTitle,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	DialogActions,
	IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CircleIcon from "@mui/icons-material/Circle";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { QuestionText } from "../ProductQuestions/questionText";
import { regexGSOF } from "../../helper/constants";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { closeDialog } from "../../services/features/dialogSlice/dialogSlice";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description"; // Word
import ArticleIcon from "@mui/icons-material/Article"; // Word
import TableChartIcon from "@mui/icons-material/TableChart";   // Excel
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BlockIcon from "@mui/icons-material/Block";

// Define the interface for referral form values.
interface ReferralValues {
	reason: string;
	comments: string;
	files: File[];
}

const initialValues: ReferralValues = {
	reason: "",
	comments: "",
	files: [],
};

/**
 * Component for rendering a referral modal to submit quote referrals.
 * @param {Object} props - Props for the component.
 * @param {boolean} props.open - Controls whether the modal is open or closed.
 * @param {Function} props.handleOpen - Function to open the modal (may not be used within the component).
 * @param {Function} props.handleClose - Function to close the modal.
 * @param {Function} props.handleReferral - Function to handle the submission of a referral.
 * @param {string} props.title - The title displayed at the top of the modal.
 * @param {string} props.subtitle - Subtitle or additional information below the title.
 * @param {boolean} props.isFilePick - Determines whether the file input field is displayed.
 * @param {string} props.actionTitle - Title for the action the user is expected to perform within the modal.
 * @param {string[]} props.referralQuestions - An array of questions that require a referral, displayed in a list.
 */

const ReferQuoteModal = ({
	handleReferral,
	title,
	subtitle,
	isFilePick,
	referralQuestions,
}: any) => {
	const dispatch = useDispatch();

	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const resetInput = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const openState = useSelector((state: RootState) => state.dialogSlice.open);

	const open = openState["referQuote"];

	const handleClose = (
		handleReset: (e?: React.SyntheticEvent<any, Event> | undefined) => void
	) => {
		resetInput();
		handleReset();
		dispatch(closeDialog("referQuote"));
	};

	const formatFileSize = (size: number) => {
		if (size < 1024) return `${size} B`;
		if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
		return `${(size / (1024 * 1024)).toFixed(1)} MB`;
	};

	const getFileIcon = (fileName: string) => {
		const ext = fileName.split(".").pop()?.toLowerCase();

		switch (ext) {
			case "pdf":
				return <PictureAsPdfIcon color="error" />;

			case "doc":
			case "docx":
				return <ArticleIcon sx={{ color: "blue" }} />;
			case "xls":
			case "xlsx":
			case "csv":
				return <TableChartIcon sx={{ color: "green" }} />;

			case "png":
			case "jpg":
			case "jpeg":
			case "gif":
			case "bmp":
				return <ImageIcon color="secondary" />;

			default:
				return <BlockIcon color="error" />;
		}
	};

	const ALLOWED_FILE_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx"];

	const ALLOWED_FILE_TYPES = ["PDF", "Word", "Excel"];

	const ACCEPT_FILE_TYPES = ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",");

	const MAX_TOTAL_FILE_SIZE_MB = 6;

	const MAX_TOTAL_FILE_SIZE_BYTES = MAX_TOTAL_FILE_SIZE_MB * 1024 * 1024;

	const getFileExtension = (fileName: string) => fileName.split(".").pop()?.toLowerCase() || "";

	const isAllowedFileType = (fileName: string) => ALLOWED_FILE_EXTENSIONS.includes(getFileExtension(fileName));

	const getTotalFileSizeBytes = (files: File[]) => files.reduce((sum, file) => sum + file.size, 0);

	const getTotalFileSizeMb = (files: File[]) => (getTotalFileSizeBytes(files) / (1024 * 1024)).toFixed(2);

	const isDefinedFile = (file: File | undefined): file is File => file !== undefined;

	const hasProhibitedFileTypeInList = (files: File[]) => files.some((file) => !isAllowedFileType(file.name));

	const removeDuplicateFiles = (files: File[]) => files.filter((file, index, self) =>	index ===self.findIndex((f) =>  //findIndex always returns the first match index
						f.name === file.name &&
						f.size === file.size &&
						f.lastModified === file.lastModified));
	return (
		<Formik<ReferralValues>
			initialValues={Object.assign({}, { ...initialValues })}

			validationSchema={Yup.object().shape({
				files: Yup.array()
					.of(Yup.mixed<File>())
					.test(
						"allowed-file-types",
						`Only ${ALLOWED_FILE_TYPES} files are allowed.`,
						(value) => {
							if (!value || value.length === 0) return true;
							const files = value.filter(isDefinedFile);
							return files.every((file) => isAllowedFileType(file.name));
						}
					)
					.test(
						"max-total-file-size",
						`Total file size must not exceed ${MAX_TOTAL_FILE_SIZE_MB} MB.`,
						(value) => {
							if (!value || value.length === 0) return true;
							const files = value.filter(isDefinedFile);
							return getTotalFileSizeBytes(files) <= MAX_TOTAL_FILE_SIZE_BYTES;
						}
					),
			})}

			enableReinitialize

			onSubmit={async (values, actions) => {
				// console.log("Formik values", values);
				await handleReferral(values);
				resetInput();
				actions.setValues(Object.assign({}, { ...initialValues }));
				actions.resetForm();
			}}
		>
			{({ values, handleChange, setValues, setFieldValue, errors, isSubmitting, handleReset, isValid, touched, validateField, }) => {

				const totalSizeOverLimit = getTotalFileSizeBytes(values.files) > MAX_TOTAL_FILE_SIZE_BYTES;
				const hasProhibitedFileType = hasProhibitedFileTypeInList(values.files);

				return (<Dialog
					open={open}
					onClose={(event, reason) => {
						if (reason && reason === "backdropClick" && !isSubmitting) {
							setValues(Object.assign({}, { ...initialValues }));
							handleClose(handleReset);
						}
					}}
				>
					<DialogTitle pb={"0 !important"}>{title}</DialogTitle>

					<Form>
						<DialogContent>
							<Typography component="div" variant="cardContentHeader" mb={1}>{subtitle}</Typography>
							{referralQuestions.length > 0 && (
								<Box py="0em">
									<Typography component="div" variant="cardContentHeader" mb={1}>
										These questions require a referral:
									</Typography>
									<List dense sx={{ width: "100%", py: 0 }}>
										{referralQuestions.map((q: any, i: number) => (
											<ListItem
												key={`question-option-${i}`}
												sx={{ alignItems: "flex-start", py: 0 }}
											>
												<ListItemIcon sx={{ minWidth: 16 }}>
													<CircleIcon sx={{ fontSize: 6, mt: "10px" }} />
												</ListItemIcon>

												<ListItemText
													primary={
														<QuestionText
															questionText={
																regexGSOF.test(q.questionText)
																	? "General Statement Of Fact"
																	: q.questionText
															}
														/>
													}
												/>
											</ListItem>
										))}
									</List>
								</Box>
							)}

							{isFilePick && (
								<Box pb="1em">
									<Box pb="0.5em">
										<Typography component="div" variant="cardContentHeader" mb={1} mt={1}>
											Accepted File Types and Size Limits.
											<br />
											<Typography variant="body2" color="text.primary" >
												You may upload only the following types of documents:{" "}
												<Typography component="span" fontWeight="bold" color="primary">
													{ALLOWED_FILE_TYPES.join(", ")}.
												</Typography>
											</Typography>
											<Typography variant="body2" color="text.primary">
												{" "} The total file size must not exceed{" "}
												<Typography component="span" fontWeight="bold" color="primary" mt={1}>
													{MAX_TOTAL_FILE_SIZE_MB} MB.
												</Typography>
											</Typography>
											<Typography component="div" variant="cardContentHeader" mb={1} mt={1}>
												By uploading, you confirm that you have the right to share these files and that they comply with our terms of service.
											</Typography>
										</Typography>
									</Box>

									<Box
										sx={{
											display: "flex",
											flexDirection: "column",   // ✅ stack vertically
											alignItems: "flex-start",  // ✅ align left
											gap: 1,
											border: "1px solid rgba(0, 0, 0, 0.23)",
											borderRadius: "4px",
											p: 1,
											minHeight: 56,
										}}>

										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<IconButton component="label" disabled={isSubmitting}>
												<UploadFileOutlinedIcon color="primary" />
												<input
													disabled={isSubmitting}
													ref={fileInputRef}
													style={{ display: "none" }}
													type="file"
													accept={ACCEPT_FILE_TYPES}
													multiple
													onChange={(event) => {
														if (event.target.files) {
															const selectedFiles = Array.from(event.target.files);
															const newFiles = removeDuplicateFiles([...values.files, ...selectedFiles]);
															setFieldValue("files", newFiles, true);
														}
													}}
													name="files"
												/>
											</IconButton>

											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ cursor: "pointer" }} onClick={() => fileInputRef.current?.click()}
											>
												Upload files
											</Typography>
										</Box>

										<Typography variant="body2" color="text.secondary">
											Total File Size:{" "}
											<Typography component="span" fontWeight="bold" color="primary">
												{getTotalFileSizeMb(values.files)} MB
											</Typography>
										</Typography>


										{/* Error shown here */}
										{hasProhibitedFileType && (
											<Typography variant="body2" color="error" sx={{ mt: 1 }}>
												Only PDF, Word and Excel documents are allowed.
											</Typography>
										)}

										{totalSizeOverLimit && (
											<Typography variant="body2" color="error" sx={{ mt: 1 }}>
												Total file size must not exceed 6 MB.
											</Typography>
										)}


										{/* {typeof errors.files === "string" && (
											<Typography variant="body2" color="error" sx={{ mt: 1 }}>
												{errors.files}
											</Typography>
										)} */}

										{values.files.length > 0 && (
											//<> //Use tag <> to associate the List of files and another component like <Typography> Total files size. The total ize has been moving to thetop of the list. 
											<Box sx={{ width: "100%", maxHeight: 200, overflowY: "auto" }}>

												<List dense>
													{values.files.map((file, index) => (
														<ListItem
															key={`${file.name}-${index}`}
															secondaryAction={
																<IconButton
																	edge="end"
																	aria-label={`delete ${file.name}`}
																	disabled={isSubmitting}
																	onClick={() => {
																		const updatedFiles = values.files.filter(
																			(_, i) => i !== index
																		);
																		setFieldValue("files", updatedFiles, true);

																		if (updatedFiles.length === 0) {
																			resetInput();
																		}
																	}}
																>
																	<DeleteOutlineIcon color="primary" />
																</IconButton>
															}
														>
															<ListItemIcon>
																{getFileIcon(file.name)}
															</ListItemIcon>

															<ListItemText
																primary={`${file.name} (${formatFileSize(file.size)})`}
															/>
														</ListItem>
													))}
												</List>
											</Box>
											//</>
										)}
									</Box>
								</Box>
							)}

							<Box pb="1em">
								<Box pb="0.5em">
									<Typography variant="cardSubtitle1">
										Provide any comments or reasons
									</Typography>
								</Box>
								<TextField
									placeholder="Type comments here"
									name="comments"
									multiline
									fullWidth
									disabled={isSubmitting}
									rows={4}
									variant="outlined"
									value={values.comments}
									onChange={handleChange}
								/>
							</Box>
						</DialogContent>

						<DialogActions>
							<Button variant="text" disabled={isSubmitting}
								onClick={() => {
									if (!isSubmitting) {
										setValues(Object.assign({}, { ...initialValues }));
										handleClose(handleReset);
									}
								}}
							>
								Cancel
							</Button>
							<Button variant="contained" type="submit" disabled={isSubmitting || !isValid}>
								{isSubmitting ? (
									<CircularProgress
										size={"2em"}
										sx={{
											color: "white",
										}}
									/>
								) : (
									"Send"
								)}
							</Button>
						</DialogActions>
					</Form>
				</Dialog>
				)
			}
			}
		</Formik >
	);
};

export default ReferQuoteModal;
